import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export type RowSkeletonVariant = 'avatar' | 'poster' | 'text';

export interface RowSkeletonProps {
  /** How many rows to draw. */
  count: number;
  /**
   * Leading thumb shape: a round avatar (feed/friend/list rows, the default),
   * a small 2:3 poster thumb (activity rows about a title), or none
   * (`text` — two lines only, for notification/calendar rows with no avatar).
   */
  variant?: RowSkeletonVariant;
  /** Accessible name for the `role="status"` region (translated by the caller). */
  label?: string;
  className?: string;
}

/**
 * Canonical row loading skeleton (ui-ux.md §7): a leading thumb + two text
 * lines per row, separated by space (no divider lines), parameterized by
 * count/variant rather than redrawn per screen.
 */
export function RowSkeleton({ count, variant = 'avatar', label, className }: RowSkeletonProps) {
  return (
    <div
      role="status"
      aria-label={label}
      className={cn('flex flex-col', className)}
    >
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex items-center gap-3 py-3">
          {variant === 'avatar' ? (
            <Skeleton className="size-10 shrink-0 rounded-full" />
          ) : variant === 'poster' ? (
            <Skeleton className="aspect-[2/3] w-12 shrink-0" />
          ) : null}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}
